import { useDashboardTheme } from "@/pages/MeetingDashboard/themeHelper";

interface TeamCoachingInsightsCardProps {
  data: any | null;
  loading: boolean;
  generating?: boolean;
  error: string | null;
  onViewMore: () => void;
  // onRefresh: () => void;
  onGenerate?: () => void;
}

export default function TeamCoachingInsightsCard({
  data,
  loading,
  generating,
  error,
  onViewMore,
  // onRefresh,
  onGenerate,
}: TeamCoachingInsightsCardProps) {
  const { colors, isDark } = useDashboardTheme();

  const styles = {
    section: {
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      borderRadius: 16,
      padding: "20px 24px",
      maxHeight: "420px",
      overflow: "auto" as const,
      display: "flex",
      flexDirection: "column" as const,
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: 700,
      color: colors.text,
      letterSpacing: 1.2,
      textTransform: "uppercase" as const,
    },
  };

  const insightsReady = data?.sessionCount ?? data?.insightsReady ?? data?.totalReps ?? 0;
  const insightsPending = data?.insightsPending ?? (data?.totalReps ? Math.max(0, data.totalReps - insightsReady) : 0);
  const hasInsights = data?.hasInsights || !!(data?.insights || data?.teamInsights);
  const insights = data?.insights || data?.teamInsights;

  return (
    <div style={styles.section}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="1.8">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          <span style={styles.sectionTitle}>Team Coaching Insights</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          {/* Regenerate Trigger */}
          {!loading && !generating && hasInsights && onGenerate && (
            <button
              onClick={onGenerate}
              title="Regenerate Team AI Insights"
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "rgba(167,139,250,0.1)",
                border: "1px solid rgba(167,139,250,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#A78BFA",
                marginRight: 8,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(167,139,250,0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(167,139,250,0.1)";
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
              </svg>
            </button>
          )}

          {/* <button
            onClick={onRefresh}
            title="Refresh Overview"
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "rgba(255,255,255,0.5)",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
          </button> */}
        </div>
      </div>

      {/* Loading overview */}
      {loading && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "40px 0", justifyContent: "center" }}>
          <div style={{ width: 22, height: 22, borderRadius: "50%", border: "2px solid rgba(167,139,250,0.2)", borderTopColor: "#A78BFA", animation: "spin 1s linear infinite" }} />
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>Loading team insights…</span>
        </div>
      )}

      {/* Generating AI Insights */}
      {generating && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "40px 0", justifyContent: "center" }}>
          <div style={{ width: 22, height: 22, borderRadius: "50%", border: "2px solid rgba(184,248,144,0.2)", borderTopColor: "#B8F890", animation: "spin 1s linear infinite" }} />
          <span style={{ fontSize: 13, color: "#B8F890", fontWeight: 500 }}>Generating team AI insights…</span>
        </div>
      )}

      {/* Error state */}
      {!loading && !generating && error && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, margin: "10px 0" }}>
          <div style={{
            borderRadius: 12, border: "1px solid rgba(248,113,113,0.25)",
            background: "rgba(248,113,113,0.06)", padding: "14px 16px",
          }}>
            <p style={{ fontSize: 13, color: "#fca5a5", margin: 0 }}>{error}</p>
          </div>
          {insightsReady >= 2 && onGenerate && (
            <button
              onClick={onGenerate}
              style={{
                padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: "rgba(248,113,113,0.15)", border: "1px solid rgba(248,113,113,0.3)",
                color: "#fca5a5", cursor: "pointer"
              }}
            >
              Retry Pitch Insights Generation
            </button>
          )}
        </div>
      )}

      {/* Content states */}
      {!loading && !generating && !error && (
        <>
          {/* State 1: Insufficient Reps (< 2) */}
          {data && insightsReady < 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "10px 0" }}>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", margin: 0, lineHeight: 1.5 }}>
                Generate the team overview to calculate the team-wise data.
              </p>
              <div style={{
                background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.2)",
                borderRadius: 12, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 4
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#FCA5A5", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  ⚠️ Criteria Not Met
                </div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                  At least 2 reps must have insights generated to unlock team-wide coaching (Currently {insightsReady} ready).
                </div>
              </div>
              <button
                disabled
                style={{
                  width: "100%", padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                  background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.25)",
                  border: "1px solid rgba(255,255,255,0.05)", cursor: "not-allowed",
                }}
              >
                Generate Team Insights
              </button>
            </div>
          )}

          {/* State 2: Sufficient Reps (>= 2) but Insights Not Yet Generated */}
          {data && insightsReady >= 2 && !hasInsights && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "10px 0" }}>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", margin: 0, lineHeight: 1.5 }}>
                Generate the team overview to calculate the team-wise data.
              </p>
              <div style={{
                background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.25)",
                borderRadius: 12, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 4
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#A78BFA", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  ✨ Team Overview Ready
                </div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                  You have {insightsReady} reps with insights ready. Click below to generate team-wide AI insights.
                </div>
              </div>
              {onGenerate && (
                <button
                  onClick={onGenerate}
                  style={{
                    width: "100%", padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                    background: "linear-gradient(135deg, #A78BFA, #7C3AED)", color: "#fff",
                    border: "none", cursor: "pointer", transition: "transform 0.2s ease, opacity 0.2s ease"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  Generate Team Insights
                </button>
              )}
            </div>
          )}

          {/* State 3: Insights Generated */}
          {data && hasInsights && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Info summary */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div style={{
                  background: "rgba(167,139,250,0.04)", border: "1px solid rgba(167,139,250,0.15)",
                  borderRadius: 12, padding: "10px 12px", display: "flex", justifyContent:"space-between"
                }}>
                  <span style={{ fontSize: 14, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: 0.5 }}>Reps Analyzed</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: "#A78BFA" }}>{insightsReady}</span>
                </div>
                <div style={{
                  background: "rgba(167,139,250,0.04)", border: "1px solid rgba(167,139,250,0.15)",
                  borderRadius: 12, padding: "10px 12px", display: "flex", justifyContent:"space-between"
                }}>
                  <span style={{ fontSize: 14, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: 0.5 }}>Pending Reps</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: "#A78BFA" }}>{insightsPending}</span>
                </div>
              </div>

              {/* Team Quick Win */}
              {insights?.teamQuickWin && (
                <div style={{
                  background: "rgba(167,139,250,0.07)", border: "1px solid rgba(167,139,250,0.25)",
                  borderRadius: 12, padding: "12px 14px",
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#A78BFA", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
                    ⚡ Team Quick Win
                  </div>
                  <p style={{ fontSize: 14, color: colors.text, margin: 0, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {insights.teamQuickWin}
                  </p>
                </div>
              )}

              {/* Systemic Issues Summary Count */}
              {Array.isArray(insights?.systemicIssues) && insights.systemicIssues.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(167,139,250,0.07)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 10, padding: "8px 12px" }}>
                  <span style={{ fontSize: 14, color: isDark ? "#A78BFA" : "#6366F1" }}>⚠️ Systemic issues identified:</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: isDark ? "#A78BFA" : "#6366F1" }}>{insights.systemicIssues.length}</span>
                </div>
              )}

              <button
                onClick={onViewMore}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                  background: isDark ? "linear-gradient(135deg, rgba(184,248,144,0.15), rgba(184,248,144,0.05))" : "rgba(34,197,94,0.1)",
                  color: isDark ? "#B8F890" : "#15803D", border: isDark ? "1px solid rgba(184,248,144,0.3)" : "1px solid rgba(34,197,94,0.25)", cursor: "pointer",
                  marginTop: 4,
                }}
              >
                View Full Team Report
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isDark ? "#B8F890" : "#15803D"} strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          )}

          {/* Null state */}
          {!data && (
            <div style={{ textAlign: "center", padding: "30px 0", color: "rgba(255,255,255,0.35)", fontSize: 13 }}>
              No team coaching data available.
            </div>
          )}
        </>
      )}
    </div>
  );
}